import { useCallback, useState, useEffect, useRef } from 'react';
import {
  useLoaderData,
  useParams,
  useNavigate,
  matchPath,
  useLocation,
  Location,
  useFetcher,
} from 'react-router-dom';
import { Box } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { resolveLdtOrderDetailView } from '@ldt/features/orders/utils/resolve_ldt_order_detail_view';
import { MemberRepository } from 'src/api/member_repository';
import { OrderRepository } from 'src/api/order_repository';
import { MemberManagementPage } from 'src/app/pages/member_management';
import { OrderDetailPage } from 'src/app/pages/order_detail_page';
import { OrderListPage } from 'src/app/pages/order_list_page';
import { OrderReviseReportPage } from 'src/app/pages/order_revise_report_page';
import { ModalErrorBoundary } from 'src/components/modal_error_boundary';
import { OrderTypeAndPathwaySelector } from 'src/components/order_type_and_pathway_selector';
import { SpecimenTypeSelector } from 'src/components/specimen_type_selector';
import { getPlatform } from 'src/config/environment';
import { ModalOrderForm } from 'src/distribution_platforms/features/components/order_form_chooser';
import { OrderDetailDisplayer } from 'src/distribution_platforms/features/order_details';
import { useIsLdtSalesOrCustomerSuccessUser } from 'src/hooks/use_auth';
import { useOrderingSpecimenTypes } from 'src/hooks/use_ordering_specimen_types';
import {
  CreateOrderInput,
  LdtOrderDetailFieldsFragment,
  OrderingPathway,
  SpecimenType,
} from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { OrderAction } from 'src/types/loaders';
import { notifyError } from 'src/utils';

// Component to render the background location content
const BackgroundLocationRenderer: React.FC<{
  location: Location;
  orderRepository: OrderRepository;
  memberRepository: MemberRepository;
  orderDetailDisplayer?: OrderDetailDisplayer;
}> = ({
  location,
  orderRepository,
  memberRepository,
  orderDetailDisplayer,
}) => {
  // Create URLSearchParams from the background location's search string
  const backgroundSearchParams = new URLSearchParams(location.search);
  const pathname = location.pathname;

  // Use React Router's matchPath for more robust route detection
  const orderDetailMatch = matchPath(
    { path: '/:organization/orders/:id' },
    pathname,
  );

  const orderReviseReportMatch = matchPath(
    { path: '/:organization/orders/:id/revise_report' },
    pathname,
  );

  const orderListMatch = matchPath({ path: '/:organization/orders' }, pathname);

  const membersListMatch = matchPath(
    { path: '/:organization/members' },
    pathname,
  );

  const invitesListMatch = matchPath(
    { path: '/:organization/invites' },
    pathname,
  );

  // Check if this is an order detail page first (but not edit/new routes)
  if (
    orderDetailMatch &&
    !pathname.includes('/edit') &&
    !pathname.includes('/new')
  ) {
    const orderId = orderDetailMatch.params.id;
    return orderDetailDisplayer && orderId ? (
      <OrderDetailPage
        orderRepository={orderRepository}
        orderDetailDisplayer={orderDetailDisplayer}
        orderId={orderId}
        isBackgroundMode={true}
      />
    ) : null;
  }

  // Check if this is the order revise report page
  else if (orderReviseReportMatch) {
    const orderId = orderReviseReportMatch.params.id;
    return orderId ? (
      <OrderReviseReportPage
        orderRepository={orderRepository}
        backgroundOrderId={orderId}
      />
    ) : null;
  }

  // Check if this is the orders list page (exact match, no additional path segments)
  else if (orderListMatch && !orderDetailMatch) {
    return (
      <OrderListPage
        orderRepository={orderRepository}
        backgroundSearchParams={backgroundSearchParams}
        isBackgroundMode={true}
      />
    );
  }

  // Check if this is the members list page
  else if (membersListMatch) {
    return (
      <MemberManagementPage
        memberRepository={memberRepository}
        initialTabIndex={0}
        isBackgroundMode={true}
      />
    );
  }

  // Check if this is the invites list page
  else if (invitesListMatch) {
    return (
      <MemberManagementPage
        memberRepository={memberRepository}
        initialTabIndex={1}
        isBackgroundMode={true}
      />
    );
  }

  return <Box>404 - page not found for path: {pathname}</Box>;
};

// Component for LDT new orders - creates a draft and redirects to edit
const LdtNewOrderComponent: React.FC<{
  orderRepository: OrderRepository;
  onClose: () => void;
  specimenType: SpecimenType;
  orderingPathway: OrderingPathway;
}> = ({
  orderRepository: _orderRepository,
  onClose,
  specimenType,
  orderingPathway,
}) => {
  const location = useLocation();
  const fetcher = useFetcher();
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    // Only create the order once
    if (isCreating || fetcher.state !== 'idle') return;

    const draftOrder: CreateOrderInput = {
      specimenType,
      orderingPathway,
      startedAt: new Date().toISOString(),
    };

    const formData = new FormData();
    formData.append('action', OrderAction.CREATE);
    formData.append('orderData', JSON.stringify(draftOrder));

    setIsCreating(true);
    fetcher.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [isCreating, fetcher, specimenType, orderingPathway, location.pathname]);

  // Handle the response from the order creation
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data && isCreating) {
      const result = fetcher.data as {
        success: boolean;
        order?: LdtOrderDetailFieldsFragment;
        action: OrderAction;
        error?: string;
      };

      if (!result.success) {
        notifyError(
          'The order could not be created at this time. Please try again later.',
        );
        onClose();
      }
    }
  }, [fetcher.data, fetcher.state, isCreating, onClose]);

  return <Box />;
};

/**
 * Before showing the form, we may need to select the specimen type and/or ordering pathway.
 * Shows both selectors together in a single dialog (ordering pathway on top for CS/Sales users).
 * Shows the specimen type selector only if there are multiple specimen types available.
 */
const FormSelector: React.FC<{
  formType: 'new' | 'edit';
  orderRepository: OrderRepository;
  onClose: () => void;
}> = ({ formType: formType, orderRepository, onClose }) => {
  const [selectedOrderingPathway, setSelectedOrderingPathway] =
    useState<OrderingPathway>(OrderingPathway.DefaultNonReflexive);
  const [selectedSpecimenType, setSelectedSpecimenType] =
    useState<SpecimenType | null>(null);
  const { enabledSpecimenTypes, hasMultipleSpecimenTypes } =
    useOrderingSpecimenTypes();
  const isCSOrSalesUser = useIsLdtSalesOrCustomerSuccessUser();

  if (formType === 'edit') {
    return (
      <EditOrderComponent orderRepository={orderRepository} onClose={onClose} />
    );
  }

  // Handle no specimen types available
  if (enabledSpecimenTypes.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
        }}
      >
        <Box>No test types are available for your current region.</Box>
      </Box>
    );
  }

  // For LDT new orders, we need to create a draft and redirect to edit
  if (getPlatform() === DistributionPlatform.Ldt) {
    // Show combined selector if:
    // 1. Multiple specimen types available, OR
    // 2. CS/Sales user (needs pathway selection)
    // AND user hasn't selected yet
    const shouldShowSelector =
      (hasMultipleSpecimenTypes || isCSOrSalesUser) &&
      selectedSpecimenType === null;

    if (shouldShowSelector) {
      return (
        <OrderTypeAndPathwaySelector
          isOpen={true}
          specimenTypes={enabledSpecimenTypes}
          isCSOrSalesUser={isCSOrSalesUser}
          onSelect={(specimenType, pathway) => {
            setSelectedSpecimenType(specimenType);
            setSelectedOrderingPathway(pathway);
          }}
          onCancel={onClose}
        />
      );
    }

    // Single specimen type and user has selected (or no selector needed) - create draft and redirect
    const testType = selectedSpecimenType || enabledSpecimenTypes[0].type;
    const pathway =
      selectedOrderingPathway || OrderingPathway.DefaultNonReflexive;
    return (
      <LdtNewOrderComponent
        orderRepository={orderRepository}
        onClose={onClose}
        specimenType={testType}
        orderingPathway={pathway}
      />
    );
  } else if (getPlatform() === DistributionPlatform.Samd) {
    // For SaMD new orders, show specimen type selector if needed, then show form
    if (hasMultipleSpecimenTypes && selectedSpecimenType === null) {
      return (
        <SpecimenTypeSelector
          isOpen={true}
          specimenTypes={enabledSpecimenTypes}
          onSelect={(specimenType) => setSelectedSpecimenType(specimenType)}
          onCancel={onClose}
        />
      );
    }
  }

  // Single specimen type or user has selected one - show the form directly
  const testType = selectedSpecimenType || enabledSpecimenTypes[0].type;
  return (
    <ModalOrderForm
      isOpen={true}
      testType={testType}
      onClose={onClose}
      orderRepository={orderRepository}
    />
  );
};

// Edit component that uses loader data
const EditOrderComponent: React.FC<{
  orderRepository: OrderRepository;
  onClose: () => void;
}> = ({ orderRepository, onClose }) => {
  const loaderData = useLoaderData() as {
    queryKey: string[];
    orderId: string;
  };

  // Get the order from the query cache using the query key from the loader
  const { data: order } = useSuspenseQuery({
    queryKey: ['order', loaderData.orderId],
    queryFn: async () => {
      return orderRepository.getOrderById(loaderData.orderId, {
        view: resolveLdtOrderDetailView(),
      });
    },
  });

  if (!order) {
    return <Box>Order not found</Box>;
  }

  return (
    <ModalOrderForm
      isOpen={true}
      order={order!}
      onClose={onClose}
      orderRepository={orderRepository}
    />
  );
};

// Order Form Page Renderer
export const OrderFormPage: React.FC<{
  formType: 'new' | 'edit';
  orderRepository: OrderRepository;
  memberRepository: MemberRepository;
  orderDetailDisplayer?: OrderDetailDisplayer;
}> = ({
  formType: modalType,
  orderRepository,
  memberRepository,
  orderDetailDisplayer,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { organization } = useParams();

  // Extract background location from React Router state
  // This background location is automatically preserved during action redirects,
  // so when /orders/new redirects to /orders/:id/edit, the same background
  // location (with query params) is maintained without manual prop passing
  const backgroundLocation = location.state?.background as Location | undefined;

  // Preserve the background location in a ref so it doesn't get lost during re-renders
  const backgroundLocationRef = useRef<Location | undefined>(
    backgroundLocation,
  );

  // Update the ref when backgroundLocation changes, but only if it's not undefined
  if (backgroundLocation) {
    backgroundLocationRef.current = backgroundLocation;
  }

  const handleCloseModal = useCallback(() => {
    // Use the ref for consistency
    const savedBackgroundLocation = backgroundLocationRef.current;
    if (savedBackgroundLocation) {
      navigate(
        savedBackgroundLocation.pathname +
          (savedBackgroundLocation.search || ''),
      );
    } else {
      // For direct navigation, go to orders list as fallback
      navigate(`/${organization}/orders`);
    }
  }, [navigate, organization]);

  return (
    <>
      {/* Background content - renders in the main page area */}
      {backgroundLocationRef.current ? (
        <BackgroundLocationRenderer
          location={backgroundLocationRef.current}
          orderRepository={orderRepository}
          memberRepository={memberRepository}
          orderDetailDisplayer={orderDetailDisplayer}
        />
      ) : (
        // Fallback for direct navigation to modal routes
        <OrderListPage
          orderRepository={orderRepository}
          backgroundSearchParams={new URLSearchParams(location.search)}
          isBackgroundMode={true}
        />
      )}
      {/* Modal overlay - positioned over entire app */}
      <ModalErrorBoundary
        key={location.pathname} // Reset error boundary when route changes
        onClose={handleCloseModal}
      >
        <FormSelector
          formType={modalType}
          orderRepository={orderRepository}
          onClose={handleCloseModal}
        />
      </ModalErrorBoundary>
    </>
  );
};
